"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.initFeatureFlags = exports.getConsentedDeviceId = void 0;
const consent_1 = require("@commercial-website/consent");
const launchdarkly_js_client_sdk_1 = require("launchdarkly-js-client-sdk");
const device_id_1 = require("./device-id");
const store_1 = require("./store");
const consentGate = {
    hasAdvertisingConsent: consent_1.hasAdvertisingConsent,
    onConsentChange: consent_1.hubspotAPI.addPrivacyConsentListener,
};
/**
 * The LD `device` context key, for attributing events sent outside the LD SDK (e.g. Segment
 * metrics) to the same context LD evaluated flags with. `undefined` without advertising consent.
 */
const getConsentedDeviceId = () => (0, device_id_1.getPersistedDeviceId)(consentGate);
exports.getConsentedDeviceId = getConsentedDeviceId;
let started = false;
// The newest client while it is still initialising. Cleared once it is published or superseded.
let pending;
// Whether the client currently in `$ldClient` was created with events enabled.
let publishedSendsEvents = false;
const buildOptions = (config, sendEvents) => ({
    bootstrap: 'localStorage',
    diagnosticOptOut: true,
    fetchGoals: false,
    sendLDHeaders: false,
    sendEvents,
    // launchdarkly-react-client-sdk always forced this on. Without it every allFlags() call would
    // send an evaluation event per flag, inflating experiment exposures.
    sendEventsOnlyForVariation: true,
    streaming: false,
    baseUrl: config.baseUrl,
    streamUrl: config.streamUrl,
    eventsUrl: config.eventsUrl,
});
const publish = (client, sendEvents, settled) => {
    const previous = store_1.$ldClient.get()?.client;
    store_1.$ldClient.set({ client, settled });
    publishedSendsEvents = sendEvents;
    if (previous !== client)
        void previous?.close();
};
// Settled = ready OR failed: like the React SDK, a failed client is still published, and its
// variation() calls return the caller's default.
const settle = (client) => client.waitForInitialization().catch(() => undefined);
const startClient = (config, sendEvents) => {
    // A still-initialising client from an earlier consent state is never published. Closing it
    // right away matters when it has events enabled and consent was just revoked.
    void pending?.close();
    pending = undefined;
    const published = store_1.$ldClient.get();
    // Consent flipped back before the replacement settled: the published client already matches.
    if (published && publishedSendsEvents === sendEvents)
        return;
    const client = (0, launchdarkly_js_client_sdk_1.initialize)(config.clientSideID, { kind: 'device', key: (0, device_id_1.resolveDeviceId)(consentGate), anonymous: false }, buildOptions(config, sendEvents));
    // Consent revoked: the published client sends events, so swap it out now instead of once the
    // replacement settles. Flags may read cached or default values until the new client is ready;
    // re-publishing it once settled makes hooks re-read them.
    if (published && !sendEvents) {
        publish(client, sendEvents, false);
        void settle(client).then(() => {
            // Already replaced by a newer client (consent granted again and that client settled).
            if (store_1.$ldClient.get()?.client !== client)
                return;
            publish(client, sendEvents, true);
        });
        return;
    }
    pending = client;
    void settle(client).then(() => {
        // Superseded (and already closed) by a newer consent state.
        if (client !== pending)
            return;
        pending = undefined;
        publish(client, sendEvents, true);
    });
};
/**
 * Starts the shared LaunchDarkly client once per browser tab (repeat calls are no-ops, so it is
 * safe across Astro ClientRouter navigations and Gatsby route changes). Events are only sent with
 * advertising consent; the SDK cannot toggle that on a live client, so a consent change starts a
 * replacement client. Granting consent swaps it in once it has settled; revoking swaps it in (and
 * closes the events-enabled client) immediately.
 */
const initFeatureFlags = (config) => {
    // No client id (local Astro dev): skip LD entirely, hooks keep returning defaults.
    if (started || typeof window === 'undefined' || !config.clientSideID)
        return;
    started = true;
    let sendEvents = (0, consent_1.hasAdvertisingConsent)();
    startClient(config, sendEvents);
    consent_1.hubspotAPI.addPrivacyConsentListener(() => {
        const next = (0, consent_1.hasAdvertisingConsent)();
        if (next === sendEvents)
            return;
        sendEvents = next;
        startClient(config, sendEvents);
    });
};
exports.initFeatureFlags = initFeatureFlags;
